import React, { useState, useEffect } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";
import { darkTheme, lightTheme } from "../../theme/Theme";
import { showErrorToast, showSuccessToast } from "../../utils/Toast/toast";
import {
  getLanguageData,
  validateVttFileBeforeUpload,
  uploadAndValidateVttFile,
  handleVttFileDelete,
  prepareLanguageOptionsForDropdown,
} from "../../utils/function.js";
import { useVttFileHandlers } from "../../hooks/useVttFileHandlers";

const ChapterManagement = ({
  chapters = [],
  setChapters = () => {},
  videoChaptersFileDetails = [],
  setVideoChaptersFileDetails = () => {},
}) => {
  const [chapterFiles, setChapterFiles] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [viewingFileId, setViewingFileId] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const isDarkTheme = useSelector((state) => state.theme);

  // Load language options on component mount - filter for English only
  useEffect(() => {
    const fetchLanguageOptions = async () => {
      try {
        const allLanguages = await getLanguageData();
        const englishItems = allLanguages.filter(
          (item) =>
            item.isActive !== false &&
            item.isActive !== true &&
            item.name?.includes("English")
        );
        setLanguageOptions(englishItems);
        // Automatically set the first English language as selected
        if (englishItems.length > 0) {
          setSelectedLanguage(englishItems[0]);
        }
      } catch (error) {
        console.error("Error fetching language options:", error);
        showErrorToast("Failed to load language options");
      }
    };

    fetchLanguageOptions();
  }, []);

  // Update chapterFiles when videoChaptersFileDetails changes
  useEffect(() => {
    if (videoChaptersFileDetails && videoChaptersFileDetails.length > 0) {
      setChapterFiles(videoChaptersFileDetails);
    }
  }, [videoChaptersFileDetails]);

  const getThemeColor = (lightColor, darkColor) =>
    isDarkTheme ? darkColor : lightColor;

  const handleFileUpload = async (acceptedFiles, languageId) => {
    const file = acceptedFiles[0];

    if (!file) return;

    // Validate file before upload
    const validation = validateVttFileBeforeUpload(file, 10);
    if (!validation.isValid) {
      showErrorToast(validation.error);
      return;
    }

    // Check if a file with the same language already exists
    const existingFile = chapterFiles.find(
      (file) => file.languageId === languageId
    );
    if (existingFile) {
      const languageName =
        languageOptions.find((lang) => lang.id === languageId)?.name ||
        "English";
      showErrorToast(
        `A chapter file for ${languageName} already exists. Please delete the existing file first or choose a different language.`
      );
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadAndValidateVttFile(
        file,
        languageId,
        languageOptions,
        "chapter"
      );

      if (!result.success) {
        showErrorToast(result.error);
        setIsUploading(false);
        return;
      }

      const newChapterFile = result.fileData;

      setChapterFiles((prev) => [...prev, newChapterFile]);

      // Update parent component's videoChaptersFileDetails
      if (typeof setVideoChaptersFileDetails === "function") {
        setVideoChaptersFileDetails((prev) => [...prev, newChapterFile]);
      }

      if (typeof setChapters === "function") {
        // Create the simplified array structure for API
        const chapterDataItem = {
          fileId: result.fileId,
          languageId: languageId,
        };
        setChapters((prev) => [...prev, chapterDataItem]);
      }
    } catch (error) {
      console.error("Error uploading chapter file:", error);
      showErrorToast("Failed to upload chapter file");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileDelete = async (fileId) => {
    setDeletingFileId(fileId);

    try {
      const result = await handleVttFileDelete(
        fileId,
        chapterFiles,
        setChapterFiles,
        setVideoChaptersFileDetails,
        setChapters,
        "chapter-file-input"
      );

      if (result.success) {
        showSuccessToast("File deleted successfully");
      } else {
        showErrorToast(result.error);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      showErrorToast("Failed to delete file. Please try again.");
    } finally {
      setDeletingFileId(null);
    }
  };

  // Use shared handlers from custom hook
  const { handleLanguageSelect, handleFileInputChange, handleViewDocument } =
    useVttFileHandlers({
      handleFileUpload,
      selectedLanguage,
      setSelectedLanguage,
      setViewingFileId,
    });

  const handleUploadClick = () => {
    // Prevent upload if English is already uploaded
    if (chapterFiles.length > 0) {
      return;
    }
    // Trigger file input - language is automatically set to English
    document.getElementById("chapter-file-input").click();
  };

  const languageOptionsForDropdown = prepareLanguageOptionsForDropdown(
    languageOptions,
    chapterFiles
  );

  // Check if English is uploaded - disable upload button if any file exists
  const isEnglishUploaded = chapterFiles.length > 0;

  // Common button styles to avoid duplication
  const uploadButtonStyles = {
    borderRadius: "20px",
    fontWeight: 600,
    fontSize: { xs: "11px", sm: "12px" },
    py: { xs: 0.75, sm: 1 },
    px: { xs: 3, sm: 4 },
    textTransform: "none",
    backgroundColor: "#1976D2",
    whiteSpace: "nowrap",
    width: { xs: "100%", sm: "auto" },
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "#1565C0",
    },
    "&:disabled": {
      backgroundColor: "#E0E0E0",
      color: "#9E9E9E",
    },
  };

  // Reusable upload button component
  const UploadButton = () => (
    <Button
      variant="contained"
      onClick={handleUploadClick}
      disabled={isEnglishUploaded || isUploading}
      sx={uploadButtonStyles}
      startIcon={
        <UploadOutlinedIcon sx={{ fontSize: { xs: "16px", sm: "18px" } }} />
      }
    >
      Upload chapter file
    </Button>
  );

  return (
    <Box
      data-testid="chapter-management"
      sx={{
        width: "100%",
        mb: { xs: 2, sm: 3 },
        ml: { xs: 1, sm: 2 },
        pr: { xs: 1, sm: 2 },
      }}
    >
      {/* Chapter document section */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "16px", sm: "18px" },
            fontWeight: 600,
            color: getThemeColor(
              lightTheme.textColorLayer1,
              darkTheme.textColorLayer1
            ),
            mb: { xs: 1.5, sm: 2 },
            fontFamily: "inherit",
          }}
        >
          Chapter document
        </Typography>

        {chapterFiles.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 2 },
              mb: 2,
              width: {
                xs: "100%",
                sm: "100%",
                md: "100%",
                lg: "50%",
                xl: "40.5%",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 },
                minWidth: 0,
                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                flexWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              <DescriptionIcon
                sx={{
                  color: "#666",
                  fontSize: { xs: "18px", sm: "20px" },
                  flexShrink: 0,
                }}
              />
              <Typography
                title={chapterFiles[0].displayName || chapterFiles[0].fileName}
                sx={{
                  fontSize: { xs: "13px", sm: "14px" },
                  fontFamily: "inherit",
                  color: getThemeColor(
                    lightTheme.textColorLayer1,
                    darkTheme.textColorLayer1
                  ),
                  minWidth: 0,
                  maxWidth: {
                    xs: "150px",
                    sm: "200px",
                    md: "400px",
                    lg: "400px",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto",
                }}
              >
                {chapterFiles[0].displayName || chapterFiles[0].fileName}
              </Typography>
              <Button
                variant="text"
                onClick={() =>
                  handleViewDocument(chapterFiles[0].fileId, chapterFiles[0].id)
                }
                disabled={viewingFileId === chapterFiles[0].id}
                sx={{
                  color: "#1976D2",
                  textTransform: "none",
                  fontSize: { xs: "12px", sm: "14px" },
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  gap: 0.5,
                  px: { xs: 1, sm: 1.5 },
                  flexShrink: 0,
                  ml: { xs: 0, sm: 1 },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                  "&:disabled": {
                    color: "#9E9E9E",
                  },
                }}
                endIcon={
                  <ArrowForwardIcon
                    sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                  />
                }
              >
                {viewingFileId === chapterFiles[0].id
                  ? "Opening..."
                  : "View document"}
              </Button>
              <IconButton
                onClick={() => handleFileDelete(chapterFiles[0].id)}
                disabled={deletingFileId === chapterFiles[0].id}
                sx={{
                  color: "error.main",
                  flexShrink: 0,
                  padding: { xs: "8px", sm: "12px" },
                  ml: { xs: 0.5, sm: 1 },
                  "&:disabled": {
                    color: "#9E9E9E",
                  },
                }}
              >
                <DeleteOutlineOutlinedIcon
                  sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                />
              </IconButton>
              <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                <UploadButton />
              </Box>
            </Box>
            <Box
              sx={{ display: { xs: "flex", sm: "none" } }}
              style={{ width: "100%" }}
            >
              <UploadButton />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 2 },
              mb: 2,
              // width: {xs: "100%", sm: "100%", md: "100%", lg: "auto", xl: "40.5%"}
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 },
                flex: { xs: "1 1 100%", sm: "1 1 auto" },
              }}
            >
              <DescriptionIcon
                sx={{
                  color: "#666",
                  fontSize: { xs: "18px", sm: "20px" },
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "13px", sm: "14px" },
                  fontFamily: "inherit",
                  color: getThemeColor(
                    lightTheme.textColorLayer4,
                    darkTheme.textColorLayer4
                  ),
                }}
              >
                No chapter file uploaded
              </Typography>
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  gap: { xs: 0.5, sm: 1 },
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  flexShrink: 0,
                }}
              >
                <UploadButton />
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                gap: { xs: 0.5, sm: 1 },
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                flexShrink: 0,
              }}
            >
              <UploadButton />
            </Box>
          </Box>
        )}

        <input
          id="chapter-file-input"
          type="file"
          accept=".vtt"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
          disabled={isEnglishUploaded}
        />
      </Box>
    </Box>
  );
};

export default ChapterManagement;
